import * as React from "react";
import { FormControlProps } from "./types";
import { useData, useDataValue, useErrors } from "../hooks/useData";
import { Label } from "./Label";
import { ErrorsDisplay } from "./Errors";

export type AutoComplete = 
	"new-password" | "one-time-code" | "username" | "current-password" | "off" 
	| "cc-number" | "cc-name" | "cc-exp-month" | "cc-exp-year" | "cc-csc"
;

export type TextProps = FormControlProps & {

    autoComplete?: AutoComplete,

    isMultiline?: boolean,
	isWide?: boolean,
	
	size?: number,
	max?: number,

	wide?: boolean,

	dontRemoveSpaces?: boolean,

	typeIndicator?: "dollars" | "mm" | "percent" | string,
	indicatorSide?: "left" | "right",

    type?: "number" | "text" | "password",

	required?: boolean,

    // gives a chance to inspect and modify the value and return something else if required
    onChangeValue?: (newValue: string) => any,
    onFormatValue?: (value: string) => string,

    onChange?: (e: any, value: string) => void,

	onFocus?: (e: any) => void,
	onBlur?: (e: any) => void,

}

export function Text(props: TextProps) {
    const data = useData();

	let value = useDataValue(data, props.prop);
	const errors = useErrors(data, props.prop, props.errorsDisabled);

	function checkIfChanged(e: any, val: string) {
        const {
            prop,
            onChangeValue,
        } = props;

		if (onChangeValue) {
            const result = onChangeValue(val);
            if (result !== undefined) {
                val = result;
            }
        }

        if (data) {
            data.setValue(prop, val);
        }

        if (props.onChange) props.onChange(e, val);
	}

    function handleInputChange(e: any) {
        var val = e.target.value;
		checkIfChanged(e, val);
    }

	function handleBlur(e: any) {

		if (props.onBlur) props.onBlur(e);

		// force trim spaces
		const {
			dontRemoveSpaces,
        } = props;

		if (dontRemoveSpaces) return;

        var val = e.target.value;

		if (val && val.length > 0) {
			const trimmed = val.trim();
			if (trimmed !== val) {
				checkIfChanged(e, trimmed);
			}
		}
	}

	const hasErrors = errors && errors.length > 0;

    const {
        className,
        disabled,
        autoComplete,
        placeholder,
        type,

        onFormatValue,
    } = props;

	if (onFormatValue) {
        const result = onFormatValue(value);
        if (result !== undefined) {
            value = result;
        }
    }

    // NOTE: work around for react warning about changing from uncontrolled to controlled
    if (value === undefined || value === null) {
        value = "";
    }

	let component;
	let commonProps = {
		className: 
			[
				"form-control text multiline",
				(props.isWide ? "wide": ""),
				(hasErrors ? "has-errors" : ""),
				className, 
			].join(" ")
		,
		value: value,
		disabled: disabled,
		style: {
			width: (props.size ? "auto" : undefined),
			...props.style,
		},
		placeholder: placeholder,
		autoComplete: autoComplete,
		onChange: handleInputChange,

		required: props.required,

		"data-key": props["data-key"],
		"data-index": props["data-index"],
		id: props.id,
		name: props.prop,

		onKeyDown: props.onKeyDown,

		onFocus: props.onFocus,

	};

	if (props.isMultiline) {
		component = (
			<textarea
				rows={5}
				{...commonProps}
			/>
		);
	} else {
		component = (
			<input
				{...commonProps}
				type={type}
				value={value}
				onBlur={handleBlur}
				size={props.size}
				max={props.max}
			/>
		);
	}

	let label;
	if (props.title) {
		label = 
			<Label 
				title={props.title}
				isRequired={(props.def && props.def.options) ? props.def.options.required : false}
				className={(hasErrors ? "has-errors" : "")}
			/>;
	}

	if (props.typeIndicator) {

		var indicator = null;

		switch (props.typeIndicator) {
			case "dollars":
				indicator = <span className="type-indicator">$</span>;
				break;
			default:
				let symbol = props.typeIndicator as string;
				if (symbol === "percent") symbol = "%";
				indicator = <span className="type-indicator">{symbol}</span>;
				break;

		}

		return (
			<div className={"flex group align-start "}>
				{label}
				<div className={"fill type-indicator-container " + props.indicatorSide}>
					{indicator}
					{component}
				</div>
			</div>
		)

	} else {
		return (
			<div className={"flex group align-start " + (props.wide ? "width-100pc" : "")}>
				{label}
				<div className="fill">
					{component}
					<ErrorsDisplay errors={errors} onlyDisplayItems={!props.displayAllErrors} />
				</div>
			</div>
		);
	}

}

